import moment from "moment";

import { KendoScheduler } from "@/Appointments/Components/Calendar/KendoSchedulerControl";
import { RulesetEditComponent, RulesetEditComponentDTO } from "@/Appointments/Components/Scheduling/RulesetEditComponent";
import { SearchComboServiceComponent } from "@/Appointments/Components/Scheduling/SearchComboServiceComponent";
import { SchedulerHelper } from "@/Appointments/Helpers/SchedulerHelper";
import { CalendarAppointmentDisplay } from "@/Appointments/Models/Calendar/CalendarAppointmentDisplay";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { ICalendarDisplaySettingsModel } from "@/Appointments/Models/UI/ICalendarDisplaySettingsModel";
import { ICalendarModel } from "@/Appointments/Models/UI/ICalendarModel";
import { ICalendarRulesetModel } from "@/Appointments/Models/UI/ICalendarRulesetModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { Popup } from "@/Common/Components/Modals/Popup";
import { QuestionnairePopupController } from "@/Common/Components/Questionnaire/QuestionnairePopupController";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { CalendarDisplaySettings } from "./CalendarDisplaySettings";
import { DeleteAppointmentConfirmationPopup } from "./DeleteAppointmentConfirmationPopup";

interface ICalendarProps {
    CalendarModel: ICalendarModel;
    CalendarRulesetModel: ICalendarRulesetModel;
    CalendarDisplaySettingsModel: ICalendarDisplaySettingsModel;
}

class CalendarState {
    public rulesetEditComponentDTO: RulesetEditComponentDTO;
    public deleteConfirmationPopupOpen: boolean;
    public deletionEventInfo?: kendo.data.SchedulerEvent & CalendarAppointmentDisplay;
}

class Calendar extends BaseReactComponent<ICalendarProps, CalendarState> {
    constructor(props: ICalendarProps) {
        super(props);

        const initialState = new CalendarState();
        initialState.deleteConfirmationPopupOpen = false;
        this.state = initialState;
    }

    public render(): JSX.Element {
        const FIVE_MIN_IN_MINISEC = 300000;
        const schedulerId = "MyleScheduler";
        const enablePrintAndSettingsCy = cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting &&
            cacheStore.ClinicSettings.EnableCalendarAppointmentsCountsCaching &&
            cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior;

        var calendarDisplaySettings = null;

        if (this.props.CalendarModel.get("ShowCalendarDisplaySettings")) {
            var css = "filter-component filter-component-responsive calendar-settings";
            if (this.props.CalendarDisplaySettingsModel.get("Sites").size === 1) { css += " single-site"; }
            calendarDisplaySettings = (
                <div id="calendar_display_settings_container" data-cy="calendar-displaySettingsContainer-div" onClick={this.onContainerClick} className={css}>
                    <CalendarDisplaySettings Model={this.props.CalendarDisplaySettingsModel} />
                </div>
            );
        }

        var dayButtonStyle = "button  cancel push-right-5 " + (this.props.CalendarModel.get("IsCalendarDayView") == true ? "active" : "");
        var weekButtonStyle = "button cancel push-right-5 " + (this.props.CalendarModel.get("IsCalendarWeekView") == true ? "active" : "");
        var monthButtonStyle = "button cancel " + (this.props.CalendarModel.get("IsCalendarMonthView") == true ? "active" : "");

        const formattedDateForDayView = moment(this.props.CalendarModel.get("CalendarStartDate"), null, sessionStore.locale).format("dddd, MMMM DD, YYYY");
        const shortFormattedDateForDayView = moment(this.props.CalendarModel.get("CalendarStartDate"), null, sessionStore.locale).format(sessionStore.getDateFormatFromLanguage());

        const formattedDateForWeekView = this.formatDisplayDateForWeekView(this.props.CalendarModel.get("CalendarStartDate"), this.props.CalendarModel.get("CalendarEndDate"));
        const formattedDateForMonthView = moment(this.props.CalendarModel.get("CalendarStartDate"), null, sessionStore.locale).format("MMMM YYYY");

        var dateString = formattedDateForDayView;
        var shortDateString = shortFormattedDateForDayView;

        if (this.props.CalendarModel.get("IsCalendarWeekView")) {
            dateString = formattedDateForWeekView;
            shortDateString = formattedDateForWeekView;
        }

        if (this.props.CalendarModel.get("IsCalendarMonthView")) {
            dateString = formattedDateForMonthView;
            shortDateString = formattedDateForMonthView;
        }

        var originalRuleset = this.props.CalendarRulesetModel.get("SelectedRuleset");
        var rulesetStartDate = this.props.CalendarRulesetModel.get("StartDate");
        var rulesetEndDate = this.props.CalendarRulesetModel.get("EndDate");

        let hasMinRuleSetDuration = false;
        if (rulesetStartDate && rulesetEndDate) {
            let duration = rulesetEndDate.valueOf() - rulesetStartDate.valueOf();
            if (duration >= FIVE_MIN_IN_MINISEC) {
                hasMinRuleSetDuration = true;
            }
        }

        var canSaveRuleset = hasMinRuleSetDuration;
        if (this.state.rulesetEditComponentDTO) {
            canSaveRuleset = this.state.rulesetEditComponentDTO.canSave && hasMinRuleSetDuration;
        }

        let rulesetEditPopupStyle: React.CSSProperties = { width: "765px" };
        if (this.props.CalendarRulesetModel.get("IsRulesetCollisionPopupOpen")) // Warning
            rulesetEditPopupStyle.zIndex = 25; // Behind overlay

        return (
            <div className="workspace">
                {this.props.CalendarModel.get("OpenAlreadyBookedPopup") === true && (
                    <Popup cssClass="double-booking" title={cacheStore.GetString("WarningLabel")} onCloseDelegate={this.onCloseAlreadyBooked.bind(this)}>
                        <div className="p-body">
                            <span>{cacheStore.GetString("AlreadyBookedAppointmentLabel")}</span>
                        </div>
                        <div className="p-footer">
                            <div className="text-right">
                                <button className="btn tbn-primary" onClick={this.onCloseAlreadyBooked.bind(this)}>{cacheStore.GetString("CloseLabel")}</button>
                            </div>
                        </div>
                    </Popup>
                )}
                <div className="myle-page-header">
                    <div className="cal-header-wrp">
                        <div className="cal-header flx">
                            <div className="flx-col a">
                                <button className={dayButtonStyle} onClick={this.onDayButtonClick.bind(this)} data-cy="calendar-onDay-button">{cacheStore.GetString("DayLabel")}</button>
                                <button className={weekButtonStyle} onClick={this.onWeekButtonClick.bind(this)} data-cy="calendar-onWeek-button">{cacheStore.GetString("WeekLabel")}</button>
                                <button className={monthButtonStyle} onClick={this.onMonthButtonClick.bind(this)} data-cy="calendar-onMonth-button">{cacheStore.GetString("MonthLabel")}</button>
                            </div>
                            <div className="flx-col b">
                                <ButtonControl
                                    variant="brand"
                                    onClick={() => sessionStore.dispatch(LocalOperations.Calendar.CalendarPage_PreviousDate_Click)}
                                    label="<"
                                />

                                <span className="inline-block hide-for-narrow-screen" style={{ margin: "0 10px", minWidth: "160px" }} data-cy="calendar-date-string">{dateString}</span>
                                <span className="inline-block show-for-narrow-screen" style={{ margin: "0 10px", minWidth: "160px" }}>{shortDateString}</span>
                                <ButtonControl
                                    variant="brand"
                                    onClick={() => sessionStore.dispatch(LocalOperations.Calendar.CalendarPage_NextDate_Click)}
                                    label=">"
                                    dataCy="calendar-next-button"
                                />
                            </div>
                            <div className="flx-col c">
                                <div className="flex-container" style={{ justifyContent: "flex-end" }}>
                                    <div style={{ marginLeft: "50px" }}>
                                        <RefreshButton className="button-margin-right-refresh-cal" onClickOperation={LocalOperations.Calendar.Calendar_Refresh_Click} />
                                    </div>
                                    <button data-cy={enablePrintAndSettingsCy ? "calendar-print" : undefined} className="button control push-right-5" onClick={this.onPrintButtonClick.bind(this)}><i className="fi-print col-lg-icon">&nbsp; </i></button>
                                    <button className="button control push-right-5" onClick={this.onPAButtonClick.bind(this)}>PA</button>
                                    <SettingsButton dataCyAttribute={enablePrintAndSettingsCy ? "calendar-settings" : undefined} onClickOperation={LocalOperations.Calendar.Calendar_Settings_Click} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ minHeight: "700px", height: "calc(100vh - 120px)" }}>
                    <div className="myle-doc-tb main-content myle-form inherit-height" data-cy="calendar-div" style={{ position: "relative", height: "99%" }}>
                        {calendarDisplaySettings}
                        <KendoScheduler
                            ControlId={schedulerId}
                            CalendarModel={this.props.CalendarModel}
                            CalendarDisplaySettingsModel={this.props.CalendarDisplaySettingsModel}
                            openDeletionPopup={(deletionEventInfo?: kendo.data.SchedulerEvent & CalendarAppointmentDisplay) => this.setState({ deletionEventInfo, deleteConfirmationPopupOpen: true })}
                        />
                        <div id="caldetails" className="cal-details" />
                        <input type="hidden" id="schfix" />
                    </div>
                </div>

                {this.props.CalendarRulesetModel.get("IsRulesetPopupOpen") && originalRuleset != null &&
                    this.renderRuleSetPopup(originalRuleset, rulesetStartDate, rulesetEndDate, canSaveRuleset)
                }

                {this.props.CalendarModel.get("SelectedQuestionniare") && (
                    <QuestionnairePopupController
                        questionnaire={this.props.CalendarModel.get("SelectedQuestionniare")}
                        onClose={() => sessionStore.dispatch(LocalOperations.Calendar.ShowQuestionnaire, { showQuestionnaire: false })}
                        onSubmitted={result => sessionStore.dispatch(LocalOperations.Calendar.OnQuestionnaireSubmitted, { result })}
                    />
                )}

                {this.state.deleteConfirmationPopupOpen && (
                    <DeleteAppointmentConfirmationPopup
                        deletionEventInfo={this.state.deletionEventInfo}
                        onClose={() => this.closeDeletionConfirmationPopup()}
                        onDelete={() => this.onSchedulerRemoveCalendarElement(this.state.deletionEventInfo)}
                    />
                )}
            </div>
        );
    }

    private closeDeletionConfirmationPopup(): void {
        this.setState({ deleteConfirmationPopupOpen: false });
    }

    private renderRuleSetPopup(originalRuleset: PredefinedRuleSetModel, rulesetStartDate: Date, rulesetEndDate: Date, canSaveRuleset: boolean): JSX.Element {
        const bodyToUse = this.props.CalendarRulesetModel.get("IsAddServicePopupOpen")
            ? this.renderAddServicePopupBody()
            : this.renderRuleSetPopupBody(originalRuleset, rulesetStartDate, rulesetEndDate, canSaveRuleset);

        return (
            <div>
                <Popup
                    style={{ width: "830px", left: "50%", marginLeft: "-415px" }}
                    draggable={false}
                    title={this.props.CalendarRulesetModel.get("IsAddServicePopupOpen")
                        ? cacheStore.GetString("AddServicesLabel")
                        : cacheStore.GetString("EditRulesetLabel")
                    }
                    onCloseDelegate={() => this.props.CalendarRulesetModel.get("IsAddServicePopupOpen") ? this.onCloseAddServicePopup() : this.onCloseRulesetPopup()}
                >
                    {bodyToUse}
                </Popup>
                {this.renderdoubleBookingPopupError()}
                {this.renderMinErrorPopup()}
            </div>
        );
    }

    private renderRuleSetPopupBody(originalRuleset: PredefinedRuleSetModel, rulesetStartDate: Date, rulesetEndDate: Date, canSaveRuleset: boolean): JSX.Element {
        const shouldShowTimeUpdate = !!originalRuleset?.Model;

        const rulesetEmployee = shouldShowTimeUpdate
            ? cacheStore.GetEmployeeByContactFacetId(originalRuleset.Model.EmployeeId)
            : null;

        const isComplex = SchedulerHelper.isServiceListComplex(this.state.rulesetEditComponentDTO?.selectedComboServiceList);

        return (
            <div>
                <div className="p-body " style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {shouldShowTimeUpdate && (
                        <div className="flex-container" style={{ margin: "20px 0px" }}>
                            <div style={{ justifyContent: "flex-start" }}>
                                <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                    <div className="open-schedule-divlabel">
                                        <label className="open-schedule-label">{cacheStore.GetString("FromLabel")}</label>
                                    </div>
                                    <div style={{ width: "128px", margin: "0 5px" }}>
                                        <TimeSelector
                                            interval={5}
                                            id="new_sched_starttime"
                                            cssClass="rounded"
                                            date={rulesetStartDate}
                                            onChangeCallback={(_date: Date) => this.updateStartHourState(_date)}
                                            disabled={false}
                                        />
                                    </div>
                                    <div className="open-schedule-divlabel" style={{ width: "65px", paddingLeft: "50px" }}>
                                        <label className="open-schedule-label">{cacheStore.GetString("ToLabel")}</label>
                                    </div>
                                    <div style={{ width: "128px", margin: "0 5px" }}>
                                        <TimeSelector
                                            interval={5}
                                            id="new_sched_endtime"
                                            cssClass="rounded"
                                            date={rulesetEndDate}
                                            onChangeCallback={(_date: Date) => this.updateEndHourState(_date)}
                                            disabled={false}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ justifyContent: "flex-end" }}>
                                <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                    <div style={{ minWidth: "128px", margin: "0 5px" }}>
                                        <button
                                            style={{ minWidth: "128px", width: "auto" }}
                                            className="btn btn-default middle-button"
                                            onClick={this.handleStartOverClick.bind(this)}
                                        >
                                            {" "}{cacheStore.GetString("StartOverLabel")}{" "}
                                        </button>
                                    </div>
                                    <div style={{ minWidth: "128px", width: "auto", margin: "0 5px" }}>
                                        <button
                                            style={{ minWidth: "128px", width: "auto" }}
                                            className="btn btn-primary middle-button"
                                            onClick={this.handleAddServiceClick.bind(this)}
                                        >
                                            {" "}{cacheStore.GetString("UpdateServicesLabel")}{" "}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {shouldShowTimeUpdate && rulesetEmployee?.BookingHubEnabled && (
                        <div className="flex-container">
                            {cacheStore.GetString(isComplex ? "BookingHub_ScheduleWillNotBePublishedMessage" : "BookingHub_ScheduleWillBePublishedMessage")}
                        </div>
                    )}
                    <RulesetEditComponent
                        selectedComboServiceList={this.props.CalendarRulesetModel.get("RulesetForEdit")}
                        employeeId={originalRuleset.Model.EmployeeId}
                        rulesetId={originalRuleset.Model.ID}
                        notifyParentCurrentStateChanged={rulesetDTO => this.onRulesetEditChangedState(rulesetDTO)}
                    />
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <button
                            className="btn btn-default push-right-5"
                            onClick={() => this.onCloseRulesetPopup()}
                        >
                            {cacheStore.GetString("CancelLabel")}
                        </button>
                        {this.state.rulesetEditComponentDTO != null && (
                            <button
                                className="btn btn-primary"
                                disabled={!canSaveRuleset}
                                onClick={() => this.onSaveRuleset()}
                            >
                                {cacheStore.GetString("SaveLabel")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderAddServicePopupBody(): JSX.Element {
        let originalRuleset = this.props.CalendarRulesetModel.get("SelectedRuleset");
        let emp = cacheStore.GetEmployeeByContactFacetId(originalRuleset.Model.EmployeeId);
        let selectedCombos = this.state.rulesetEditComponentDTO.selectedComboServiceList;
        if (this.props.CalendarRulesetModel.get("IsStartOver"))
            selectedCombos = this.props.CalendarRulesetModel.get("SelectedComboServiceList");

        return (
            <div>
                <div className="p-body">
                    <SearchComboServiceComponent
                        selectedComboServiceList={selectedCombos}
                        selectedEmployeeContactId={emp.Contact_id}
                        selectedSiteId={originalRuleset.Model.SiteId}
                        comboServiceListSelectionChangeOperation={LocalOperations.Calendar.Ruleset_ChangeComboServiceSelection}
                        isFromOpenSchedule={true}
                    />
                </div>

                <div className="p-footer">
                    <div className="text-right">
                        <button className="btn btn-default push-right-5" onClick={() => this.onCloseAddServicePopup()}>{cacheStore.GetString("CancelLabel")}</button>
                        <button className="btn btn-primary" disabled={false} onClick={() => this.onAddSelectedComboService()}>{cacheStore.GetString("UpdateLabel")}</button>
                    </div>
                </div>
            </div>
        );
    }

    private renderdoubleBookingPopupError(): JSX.Element {
        if (!this.props.CalendarRulesetModel.get("IsRulesetCollisionPopupOpen"))
            return null;

        let dateListRender = null;
        if (this.props.CalendarRulesetModel.get("ExistingRuleDate")) {
            dateListRender = this.props.CalendarRulesetModel.get("ExistingRuleDate").map((reserved: Date, idx: number) => (
                <div key={"reservedDate_" + idx}>
                    <label>{moment(reserved).format("YYYY/MM/DD") + " " + cacheStore.GetString("LowerCaseAtLabel") + " " + moment(reserved).format("hh:mm:ss")}</label>
                </div>
            ));
        }

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => this.closeRulesetCollisionPopup()} title={cacheStore.GetString("WarningLabel")}>
                <div className="p-body">
                    <strong>{cacheStore.GetString("ErrorMessage1AddAvailabilityLabel")}</strong>
                    {dateListRender}
                    <strong>{cacheStore.GetString("ErrorMessage2AddAvailabilityLabel")}</strong>
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <button className="btn btn-default push-right-5" onClick={() => this.closeRulesetCollisionPopup()}>{cacheStore.GetString("CancelLabel")}</button>
                        <button className="btn btn-primary" onClick={() => this.onSaveRuleset(true)}>{cacheStore.GetString("ContinueLabel")}</button>
                    </div>
                </div>
            </Popup>
        );
    }

    private onSchedulerRemoveCalendarElement(event: kendo.data.SchedulerEvent & CalendarAppointmentDisplay): void {
        if (event.AppointmentId) {
            if (event.AppointmentStatusCode == "PREDEFINED") {
                sessionStore.dispatch(LocalOperations.Calendar.Appointment_RemovePredefinedAppointment, {
                    predefinedAppointmentId: event.AppointmentId
                });
            }
            else {
                sessionStore.dispatch(LocalOperations.Calendar.Appointment_RemoveAppointment, {
                    appointmentId: event.AppointmentId
                });
            }
        }
        else if (event.AppointmentStatusCode == "PREDEFINEDRULESET") {
            sessionStore.dispatch(LocalOperations.Calendar.Calendar_DeleteMyleRuleset, {
                rulesetId: event.RuleSet.ID
            });
        }

        this.closeDeletionConfirmationPopup();
    }

    public componentWillUnmount(): void {
        calendarStore.patientToPreloadOnNextAppCreation = null;
    }

    private formatDisplayDateForWeekView(startDate: Date, endDate: Date): string {
        const s = moment(startDate, null, sessionStore.locale);
        const e = moment(endDate, null, sessionStore.locale);
        return s.format("D MMM") + " - " + e.format("D MMM, YYYY");
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private onDayButtonClick(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.CalendarView_Day_Click);
        LocalEventDispatcher.dispatch(operation);
    }

    private onWeekButtonClick(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.CalendarView_Week_Click);
        LocalEventDispatcher.dispatch(operation);
    }

    private onMonthButtonClick(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.CalendarView_Month_Click);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPAButtonClick(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.Calendar_PA_Click);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPrintButtonClick(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.Calendar_MD_Click);
        LocalEventDispatcher.dispatch(operation);
    }

    private onCloseAlreadyBooked(): void {
        LocalEventDispatcher.dispatch(new LocalEvent(LocalOperations.Calendar.Calendar_OnCloseAlreadyBookedAppointment));
    }

    // --------------------------------------------------------------------------------------------------------------------------------
    // -------Ruleset management ------------------------------------------------------------------------------------------------------
    // --------------------------------------------------------------------------------------------------------------------------------

    private onRulesetEditChangedState(rulesetEditComponentDTO: RulesetEditComponentDTO): void {
        var s = new CalendarState();
        s.rulesetEditComponentDTO = rulesetEditComponentDTO;
        this.setState(s);
    }

    private onCloseRulesetPopup(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.Ruleset_ClosePopup);
        LocalEventDispatcher.dispatch(operation);
    }

    private closeRulesetCollisionPopup(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.Ruleset_CloseRulesetCollisionPopup);
        LocalEventDispatcher.dispatch(operation);
    }

    private onSaveRuleset(forceDelete: boolean = false): void {
        if (this.state.rulesetEditComponentDTO == null)
            return;

        let modifiedServices = this.state.rulesetEditComponentDTO.selectedComboServiceList;
        let originalRuleset = this.props.CalendarRulesetModel.get("SelectedRuleset");
        let emp = cacheStore.GetEmployeeByContactFacetId(originalRuleset.Model.EmployeeId);
        let startDate = this.props.CalendarRulesetModel.get("StartDate");
        let endDate = this.props.CalendarRulesetModel.get("EndDate");

        let minMaxResults = SchedulerHelper.calculateTotalMinAndMax(modifiedServices.toArray(), startDate, endDate);
        if (!minMaxResults.MinimumsValid) {
            sessionStore.dispatch(LocalOperations.Calendar.Ruleset_ShowMinErrorPopup);
            return;
        }

        // This will cause a Delete/ReInsert of the ruleset
        sessionStore.dispatch(LocalOperations.Calendar.Ruleset_SaveChanges, {
            modifiedServices: modifiedServices,
            originalRuleset: originalRuleset,
            startDate: startDate,
            endDate: endDate,
            siteIds: emp.AssociatedSites,
            forceDelete: forceDelete,
            isBookingHubAvailability: !SchedulerHelper.isServiceListComplex(modifiedServices)
        });
    }

    private renderMinErrorPopup(): JSX.Element {
        if (!this.props.CalendarRulesetModel.get("MinErrorPopupOpen"))
            return null;

        const closeMinError = (): void => {
            sessionStore.dispatch(LocalOperations.Calendar.Ruleset_ShowMinErrorPopup, { hide: true });
        };

        return (
            <Popup cssClass="double-booking" draggable={true} onCloseDelegate={() => closeMinError()} title={cacheStore.GetString("WarningLabel")}>
                <div className="p-body">
                    <label>{cacheStore.GetString("MinRuleSetErrorLabel")}</label>
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <button
                            className="btn btn-default"
                            onClick={() => closeMinError()}
                        >{cacheStore.GetString("ContinueLabel")}
                        </button>
                    </div>
                </div>
            </Popup>
        );
    }

    private onCloseAddServicePopup(): void {
        var operation = new LocalEvent(LocalOperations.Calendar.Ruleset_CloseAddServicePopup);
        LocalEventDispatcher.dispatch(operation);
    }

    private handleStartOverClick(): void {
        let operation = new LocalEvent(LocalOperations.Calendar.Ruleset_StartOverBtnClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private handleAddServiceClick(): void {
        let operation = new LocalEvent(LocalOperations.Calendar.Ruleset_OpenAddServicePopup);
        LocalEventDispatcher.dispatch(operation);
    }

    private updateStartHourState(startDate: Date): void {
        let operation = new LocalEvent(LocalOperations.Calendar.Ruleset_StartDateChanged);
        operation.AddItem("startDate", startDate);
        LocalEventDispatcher.dispatch(operation);
    }

    private updateEndHourState(endDate: Date): void {
        let operation = new LocalEvent(LocalOperations.Calendar.Ruleset_EndDateChanged);
        operation.AddItem("endDate", endDate);
        LocalEventDispatcher.dispatch(operation);
    }

    private onAddSelectedComboService(): void {
        let operation = new LocalEvent(LocalOperations.Calendar.Ruleset_AddServicePopup_AddBtn_Click);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { Calendar };
